import React, { useState, useMemo } from 'react'
import { DesignTokens } from '../types'
import { generateCSSVariables, generateTailwindConfig } from '../utils/exportUtils'

interface ExportPanelProps {
  tokens: DesignTokens
  themeName: string
  onThemeNameChange: (name: string) => void
}

type ExportTab = 'css' | 'tailwind'

export function ExportPanel({ tokens, themeName, onThemeNameChange }: ExportPanelProps) {
  const [activeTab, setActiveTab] = useState<ExportTab>('css')
  const [copied, setCopied] = useState(false)

  const cssOutput = useMemo(() => generateCSSVariables(tokens, themeName), [tokens, themeName])
  const tailwindOutput = useMemo(
    () => generateTailwindConfig(tokens, themeName),
    [tokens, themeName]
  )

  const currentOutput = activeTab === 'css' ? cssOutput : tailwindOutput

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentOutput)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea')
      textarea.value = currentOutput
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleDownload = () => {
    const filename = activeTab === 'css' ? 'design-tokens.css' : 'tailwind.config.ts'
    const blob = new Blob([currentOutput], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Export</h2>
        <input
          type="text"
          value={themeName}
          onChange={(e) => onThemeNameChange(e.target.value)}
          placeholder="Theme name"
          className="px-2 py-1 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
        <button
          onClick={() => setActiveTab('css')}
          className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'css'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          } cursor-pointer`}
        >
          CSS Variables
        </button>
        <button
          onClick={() => setActiveTab('tailwind')}
          className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'tailwind'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          } cursor-pointer`}
        >
          Tailwind Config
        </button>
      </div>

      {/* Code Output */}
      <div className="relative">
        <pre className="p-4 bg-gray-900 text-gray-100 text-xs rounded-lg overflow-auto max-h-64 font-mono">
          <code>{currentOutput}</code>
        </pre>

        {/* Actions */}
        <div className="absolute top-2 right-2 flex gap-1">
          <button
            onClick={handleCopy}
            className="p-1.5 text-gray-400 hover:text-white bg-gray-800 rounded-md transition-colors cursor-pointer"
            title="Copy to clipboard"
          >
            {copied ? (
              <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            )}
          </button>
          <button
            onClick={handleDownload}
            className="p-1.5 text-gray-400 hover:text-white bg-gray-800 rounded-md transition-colors cursor-pointer"
            title="Download file"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </button>
        </div>
      </div>

      {copied && (
        <p className="text-xs text-green-600 font-medium">Copied to clipboard!</p>
      )}
    </div>
  )
}
